import { Hash } from "lucide-react";

import { useChooseEntityConfig } from "../../choose-entity-config-context";
import { SelectableOptionList } from "../SelectableOptionList";

import { useChooseEntityVariant } from "./choose-entity-variant-context";

import { Entity } from "@/queries";
import { getEntityName } from "@/components/UnifiedRegistry/utils";

const VersionContent = ({
  isLoading,
  latestVersion,
  selectedVersion,
  readonly,
  onVersionSelect,
}: {
  isLoading: boolean;
  latestVersion: number | undefined;
  selectedVersion: number | undefined | null;
  readonly: boolean;
  onVersionSelect: (version?: number) => void;
}) => {
  const { entity, selectedEntities } = useChooseEntityVariant();
  const entityType = entity.type;
  const config = useChooseEntityConfig();
  const allowDuplicateVariants = config ? config.allowDuplicateVariants : false;

  // Get already selected versions for this entity (excluding the current one being edited)
  const alreadySelectedVersions = selectedEntities
    .filter((e: Entity) => (e.id === entity.id || e.name === entity.name) && !e.label)
    .map((e: Entity) => e.version_number);

  const versions = latestVersion
    ? [undefined, ...Array.from({ length: latestVersion }, (_, i) => latestVersion - i)]
    : [];

  const isVersionDisabled = (version: number | undefined): boolean => {
    // "All Versions" (undefined) should never be disabled when allowDuplicateVariants is enabled
    if (version === undefined && allowDuplicateVariants) {
      return false;
    }
    return alreadySelectedVersions.includes(version);
  };

  return (
    <div className="h-full overflow-hidden">
      <SelectableOptionList
        isLoading={isLoading}
        loadingMessage="Loading versions..."
        emptyStateTitle="No published versions available"
        emptyStateDescription={`There are no published versions of this ${getEntityName(
          entityType,
        ).toLowerCase()} to use.`}
        items={versions}
        selectedKey={selectedVersion === null ? null : (selectedVersion ?? "latest")}
        readonly={readonly}
        getItemKey={version => version ?? "latest"}
        // eslint-disable-next-line react/no-unstable-nested-components
        getItemLeading={() => <Hash className="h-4 w-4" aria-hidden />}
        // eslint-disable-next-line react/no-unstable-nested-components
        getItemLabel={version => (
          <div className="flex items-center gap-2">
            {version === undefined ? (
              <>
                <span className="font-medium text-gray-900">
                  {allowDuplicateVariants ? "All Versions" : "Latest"}
                </span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                  {allowDuplicateVariants ? "Select All" : "Recommended"}
                </span>
              </>
            ) : (
              <>
                <span className="font-medium text-gray-900">Version #{version}</span>
                {version === latestVersion && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                    Latest
                  </span>
                )}
              </>
            )}
          </div>
        )}
        // eslint-disable-next-line react/no-unstable-nested-components
        getItemDescription={version =>
          version === undefined && allowDuplicateVariants && latestVersion ? (
            <span className="text-xs text-gray-500">
              Matches all {latestVersion} version{latestVersion > 1 ? "s" : ""} (v1-v{latestVersion}
              )
              {alreadySelectedVersions.length > 0 &&
                ` • Replaces ${alreadySelectedVersions.length} selected version${
                  alreadySelectedVersions.length > 1 ? "s" : ""
                }`}
            </span>
          ) : undefined
        }
        isItemDisabled={isVersionDisabled}
        onItemSelect={onVersionSelect}
      />
    </div>
  );
};

export default VersionContent;
